import { createApi } from "@reduxjs/toolkit/query/react";
import { DocumentSetMetrics } from "../interfaces/documentSetMetrics";
import { setMetricsData } from "./analyticsSlice";
import { notification, NotificationArgsProps } from "antd";
import {
  parseReduxQueryError,
  customBaseQuery,
} from "../../common/reduxQuery/helpers";
import { MetricEvent } from "../interfaces/metricEvent";
import { calculateAllTotalMetrics } from "../utils/utils";
import { MetricsMode } from "../constants";

export const analyticsApi = createApi({
  reducerPath: "analyticsApi",
  baseQuery: customBaseQuery(),
  endpoints: (build) => ({
    getAllMetricsForInputs: build.query<
      DocumentSetMetrics,
      {
        startDate: string;
        endDate: string;
        filterString: string;
        metricsMode: MetricsMode;
        inputIds: Array<string>;
        documentTypeIds: Array<string>;
        notificationErrorBody: NotificationArgsProps;
        customerSearchPattern: string | null;
      }
    >({
      query: ({
        startDate,
        endDate,
        filterString,
        inputIds,
        documentTypeIds,
        metricsMode,
        customerSearchPattern,
      }) => {
        let queryPath: string;
        if (metricsMode === MetricsMode.OPERATIONAL) {
          queryPath = "inputs";
        } else if (metricsMode === MetricsMode.ADVANCED) {
          queryPath = "advanced";
        } else {
          queryPath = "users";
        }

        // Omitted rather than sent empty: Spring would bind "" as a single
        // blank element and fail to parse it as a UUID.
        const documentTypeIdsParam = documentTypeIds.length
          ? `&documentTypeIds=${documentTypeIds.join(",")}`
          : "";

        return {
          url: `/api/v1/metrics/${queryPath}?inputIds=${inputIds.join(
            ",",
          )}&startDate=${startDate}&endDate=${endDate}&filter=${filterString}&customerSearchPattern=${customerSearchPattern}${documentTypeIdsParam}`,
          method: "GET",
        };
      },
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const metrics = calculateAllTotalMetrics({ ...data });
          dispatch(setMetricsData(metrics));
        } catch (err) {
          console.error(
            `An error occurred while retrieving the metrics for the document sets: ${parseReduxQueryError(
              err,
            )}`,
          );
          notification.error(args.notificationErrorBody);
          dispatch(setMetricsData(undefined));
        }
      },
    }),
    saveMetricEvent: build.mutation<void, MetricEvent>({
      query: (metricEvent) => ({
        url: "/api/v1/metrics/events",
        body: metricEvent,
        method: "POST",
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          console.error(
            `An error occurred while saving a metric event: ${parseReduxQueryError(
              err,
            )}`,
          );
        }
      },
    }),
  }),
});

export const { useGetAllMetricsForInputsQuery, useSaveMetricEventMutation } =
  analyticsApi;
